import React, { useEffect, useRef, useState } from 'react';

interface InlineEditableTextProps {
  value: string;
  onChange: (newValue: string) => void;
  placeholder?: string;
  className?: string;
  isTitle?: boolean;
}

// Функция для обработки текста с цветовым выделением
const processTextWithHighlighting = (text: string): React.ReactNode => {
  if (!text) return text;

  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  
  const keywordPattern = /(Кадр\s*\d*:|Озвучка:|Заголовок:|Текст:|Визуал:)/g;
  let match;
  
  while ((match = keywordPattern.exec(text)) !== null) {
    if (match.index > currentIndex) {
      const beforeText = text.slice(currentIndex, match.index);
      
      const previousKeyword = parts[parts.length - 1];
      const shouldHighlightGreen = previousKeyword && 
        React.isValidElement(previousKeyword) && 
        (previousKeyword.props.children === 'Озвучка:' ||
         previousKeyword.props.children === 'Заголовок:');
      
      const shouldHighlightYellow = previousKeyword && 
        React.isValidElement(previousKeyword) && 
        previousKeyword.props.children === 'Визуал:';
      
      if (shouldHighlightGreen) {
        parts.push(
          <span key={`green-${currentIndex}`} className="text-green-600 dark:text-green-400">
            {beforeText}
          </span>
        );
      } else if (shouldHighlightYellow) {
        parts.push(
          <span key={`cyan-${currentIndex}`} className="text-cyan-600 dark:text-cyan-400">
            {beforeText}
          </span>
        );
      } else {
        parts.push(beforeText);
      }
    }
    
    parts.push(
      <span key={`keyword-${match.index}`} className="text-purple-600 dark:text-purple-400 font-medium">
        {match[0]}
      </span>
    );
    
    currentIndex = match.index + match[0].length;
  }
  
  if (currentIndex < text.length) {
    const remainingText = text.slice(currentIndex);
    
    const lastKeyword = parts[parts.length - 1];
    const shouldHighlightGreen = lastKeyword && 
      React.isValidElement(lastKeyword) && 
      (lastKeyword.props.children === 'Озвучка:' ||
       lastKeyword.props.children === 'Заголовок:');
    
    const shouldHighlightYellow = lastKeyword && 
      React.isValidElement(lastKeyword) && 
      lastKeyword.props.children === 'Визуал:';
    
    if (shouldHighlightGreen) {
      parts.push(
        <span key={`green-${currentIndex}`} className="text-green-600 dark:text-green-400">
          {remainingText}
        </span>
      );
    } else if (shouldHighlightYellow) {
      parts.push(
        <span key={`cyan-${currentIndex}`} className="text-cyan-600 dark:text-cyan-400">
          {remainingText}
        </span>
      );
    } else {
      parts.push(remainingText);
    }
  }
  
  return parts.length > 1 ? <>{parts}</> : text;
};

export const InlineEditableText: React.FC<InlineEditableTextProps> = ({
  value,
  onChange,
  placeholder = 'Нажмите для редактирования',
  className = '',
  isTitle = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [isEditing]);

  const handleViewClick = () => {
    setIsEditing(true);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  const handleTextareaBlur = () => {
    setIsEditing(false);
    onChange(localValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleTextareaBlur();
    }
  };

  return (
    <div className={`group ${className} relative cursor-text`} onClick={handleViewClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleViewClick()}>
      {!isEditing && (
        <div 
          className={`
            w-full p-1 whitespace-pre-wrap
            ${isTitle ? 'font-medium text-lg' : 'text-sm'}
            ${!localValue ? 'text-gray-400 dark:text-gray-500 italic' : ''}
          `}
        >
          {localValue ? processTextWithHighlighting(localValue) : placeholder}
        </div>
      )}
      
      {isEditing && (
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={handleTextareaChange}
          onBlur={handleTextareaBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`
            w-full resize-none overflow-hidden p-1
            outline-none border-0 focus:ring-0
            bg-transparent
            ${isTitle ? 'font-medium text-lg' : 'text-sm'}
            text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500
          `}
          style={{ height: 'auto', minHeight: isTitle ? '32px' : '24px' }}
        />
      )}
    </div>
  );
};

